import { Card, Divider, IconButton, InputAdornment, Typography } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import { useNotification, useRouteQuery, useTranslator } from '@/hooks'
import { FormProvider, useForm } from 'react-hook-form'
import { FormInput } from '@/components/form/FormInput'
import FormSelect from '@/components/form/FormSelect'
import { useAttendanceBulkSettingByCompanyIdMutation, workingTypeConfigLabel } from '@/@data/attendances/attendance'
import { yupResolver } from '@hookform/resolvers/yup'
import { attendanceBulkSettingSchema } from '@/@data/attendances/schema'
import { ActivityLogTypeEnum, IHttpError } from '@/@data'
import { useNavigate } from 'react-router-dom'
import AppLogs from '@/components/common/AppLogs'

export const SettingCompanyAttendance = ({
  setIsSettingCompanyAttendance,
  company
}: {
  setIsSettingCompanyAttendance?: (isSettingCompanyAttendance: boolean) => void
  company?: any
  isSettingCompanyAttendance?: boolean
}) => {
  const { __, __n } = useTranslator()
  // state
  const [isSetting, setIsSetting] = useState(true)
  const [isOpenLogs, setIsOpenLogs] = useState(false)

  const workingTypeOptionsLabel = workingTypeConfigLabel()
  const n = useNotification()
  const { searchQuery } = useRouteQuery()
  const navigate = useNavigate()

  useEffect(() => {
    methods.reset({
      working_rate: __n(Number(company?.working_rate) || 0) as any,
      working_type: company?.working_type || ''
    })
  }, [company])

  const workingTypeOptions = useMemo(() => {
    return Object.entries(workingTypeOptionsLabel).map(([value, label]) => ({
      value,
      label
    }))
  }, [workingTypeOptionsLabel])

  const { id } = useParams<{ id: string }>()

  const handleToggleSetting = () => {
    setIsSetting(!isSetting)
  }

  const methods = useForm<any>({
    defaultValues: {
      working_rate: 0,
      working_type: ''
    },
    resolver: yupResolver(attendanceBulkSettingSchema(__)),
    mode: 'onChange'
  })

  const { mutate: attendanceBulkSettingMutation } = useAttendanceBulkSettingByCompanyIdMutation()

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      working_rate: Number(data.working_rate),
      working_type: data.working_type
    }

    attendanceBulkSettingMutation(
      { companyId: Number(searchQuery.get('companyId')) || Number(id), data: payload as any },
      {
        onSuccess: () => {
          n.success(__('Attendance settings updated successfully'))
          navigate(`/cms/attendance`)
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  return (
    <>
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className='flex justify-between items-start gap-5'>
          <Card sx={{ borderRadius: 3, flex: 1 }}>
            <div className='p-4'>
              <div className='flex justify-between items-center'>
                <div className='flex items-center gap-3'>
                  <Button
                    variant='outlined'
                    size='small'
                    className='border-blue-500 text-blue-600 hover:border-blue-600'
                  >
                    <span className='text-[15px] font-medium max-w-[500px] whitespace-nowrap overflow-hidden text-ellipsis' title={company?.name || '-'}>{company?.name || '-'}</span>
                  </Button>
                  <Divider orientation='vertical' flexItem className='w-[2px] bg-[#2f2b3d1f]' />
                  <Typography variant='h6' className='text-[18px] font-medium'>
                    {__('Setting Company Attendance')}
                  </Typography>
                </div>
                <div className='flex items-center gap-2'>
                  <IconButton
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                    onClick={() => {
                      setIsOpenLogs(true)
                    }}
                  >
                    <i className='tabler-history text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                    onClick={() => {
                      setIsSettingCompanyAttendance?.(false)
                    }}
                  >
                    <i className='tabler-x text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                    onClick={() => {
                      handleToggleSetting()
                    }}
                  >
                    <i className='tabler-viewport-wide text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              </div>
              <div className='text-[15px] font-medium text-[#2f2b3db3] mt-3'>
                {__('This section allows you to adjust the Assumption Working Rate and define Monthly Working Days.')}
              </div>

              <div className='my-5'>
                <Typography variant='h6' className='text-[18px] font-medium'>
                  {__('Setting Company Attendance')}
                </Typography>
              </div>
              <div className='grid grid-cols-2 gap-6'>
                <div>
                  <FormInput
                    name='working_rate'
                    label={__('Assumption Working Rate')}
                    placeholder={__('Enter Rate Assumption Working Rate')}
                    control={methods.control}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position='end'>
                            <i className='tabler-percentage text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                          </InputAdornment>
                        ),
                        onInput: (e: any) => {
                          if (e.target.value < 0) {
                            e.target.value = 0
                          }

                          if (e.target.value > 100) {
                            e.target.value = 100
                          }
                        }
                      }
                    }}
                    className='w-full'
                    onClear={() => {
                      methods.setValue('working_rate', undefined)
                    }}
                  />
                </div>
                <div>
                  <FormSelect
                    name='working_type'
                    labelText={__('Weekly Work Schedule Setup')}
                    placeholder={__('Select Weekly Work Schedule Setup')}
                    options={workingTypeOptions}
                    height='38px'
                    onClear={() => {
                      methods.setValue('working_type', '')
                    }}
                  />
                </div>
              </div>
            </div>
          </Card>
          {isSetting && (
            <Card sx={{ borderRadius: 3, width: '266px' }}>
              <div className='p-4'>
                <Button
                  variant='tonal'
                  type='button'
                  size='small'
                  className='block w-full mb-3'
                  onClick={() => {
                    methods.reset()
                  }}
                >
                  <span className='text-[15px] font-medium'>{__('Reset')}</span>
                </Button>
                <Button type='submit' variant='tonal' size='small' className='block w-full bg-[#28c76f29]'>
                  <span className='text-[15px] font-medium text-[#28c76f]'>{__('Save')}</span>
                </Button>
              </div>
            </Card>
          )}
        </div>
      </form>
    </FormProvider>
    <AppLogs
      open={isOpenLogs}
      setOpen={setIsOpenLogs}
      title={__('History Menu Attendance')}
      object_type={ActivityLogTypeEnum.Attendance}
      objectId={id || searchQuery.get('companyId')}
    />
    </>
  )
}
